import React, { useEffect, useState } from 'react';
import './ReadingProgress.css';

/**
 * Where a background document read has really got to.
 *
 * Every screen that waits on a document being read shows this, fed by the
 * `progress` the server records while its job runs (backend
 * utils/reading_progress.py): the stage, which document of a batch, and which
 * page. A scan read by the page-reading service has all its pages read at once,
 * so it reports how many pages there are and no page it is "on" (page 0); a scan
 * read on the server's own processors reports each page as it goes.
 *
 * progress: { stage, page, pages, document, documents, document_name, started_at } | null
 * leaveText: what to tell the person about leaving the screen while it runs.
 * showStage: false where the screen around it already names the stage (the
 *   Knowledge Hub steps), so the panel does not repeat it.
 */
const STAGE_LABELS = {
    queued: 'Waiting to start',
    reading: 'Reading your document',
    analysing: 'Analysing the content',
    indexing: 'Making it searchable',
    filing: 'Filing it away',
    finished: 'Finishing up',
};

// How long before the panel explains that scans are slow.
const PATIENCE_NOTE_AFTER_SECONDS = 45;

const secondsSince = (iso) => {
    const started = iso ? Date.parse(iso) : NaN;
    if (Number.isNaN(started)) return null;
    return Math.max(0, Math.round((Date.now() - started) / 1000));
};

export const formatElapsed = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    if (seconds < 60) return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
};

// Every page being read together: the count is known, no single page is current.
export const readsAllPagesAtOnce = (progress) => (
    progress?.stage === 'reading' && Boolean(progress?.pages) && !progress?.page
);

export const describeStage = (progress) => {
    const stage = progress?.stage || 'queued';
    if (stage === 'reading' && progress?.documents > 1) {
        const name = progress.document_name ? ` — ${progress.document_name}` : '';
        return `Reading document ${progress.document} of ${progress.documents}${name}`;
    }
    if (readsAllPagesAtOnce(progress)) {
        return `Reading all ${progress.pages} pages`;
    }
    return STAGE_LABELS[stage] || STAGE_LABELS.reading;
};

const ReadingProgress = ({ progress, startedAt, leaveText, compact = false, showStage = true }) => {
    const [mountedAt] = useState(() => new Date().toISOString());
    const [, setTick] = useState(0);

    // Re-render once a second so the elapsed time moves between status checks.
    useEffect(() => {
        const timer = setInterval(() => setTick((value) => value + 1), 1000);
        return () => clearInterval(timer);
    }, []);

    const elapsed = secondsSince(progress?.started_at || startedAt || mountedAt);
    const hasPages = Boolean(progress?.pages) && Boolean(progress?.page);
    const percent = hasPages ? Math.min(100, Math.round((progress.page / progress.pages) * 100)) : null;
    const reading = !progress || progress.stage === 'reading' || progress.stage === 'queued';
    const showPatience = reading && elapsed !== null && elapsed >= PATIENCE_NOTE_AFTER_SECONDS;
    const headline = showStage
        ? describeStage(progress)
        : (readsAllPagesAtOnce(progress) ? `All ${progress.pages} pages at once` : '');

    return (
        <div className={`reading-progress${compact ? ' is-compact' : ''}`} role="status" aria-live="polite">
            <div className="reading-progress-head">
                <span className="reading-progress-stage">{headline}</span>
                {elapsed !== null && <span className="reading-progress-elapsed">{formatElapsed(elapsed)}</span>}
            </div>

            <div
                className={`reading-progress-track${hasPages ? '' : ' is-indeterminate'}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent === null ? undefined : percent}
            >
                <div className="reading-progress-fill" style={hasPages ? { width: `${percent}%` } : undefined} />
            </div>

            {hasPages && (
                <div className="reading-progress-pages">Page {progress.page} of {progress.pages}</div>
            )}

            {showPatience && (
                <p className="reading-progress-note">
                    Scanned documents can take a minute or more.
                    {leaveText ? ` ${leaveText}` : ''}
                </p>
            )}
        </div>
    );
};

export default ReadingProgress;
